/* eslint-disable react-refresh/only-export-components */
import {
  forwardRef,
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import type { ComponentSize } from 'src/modules/shared/types/component.type';
import {
  baseInputClasses,
  editableInputTextSizeClasses,
  inputVariantClasses,
  getDisabledClasses,
  getErrorClasses,
} from 'src/modules/shared/styles/component.styles';
import { mergeRefs } from 'src/modules/shared/utils/refs.util';
import InputError from '../input-error';
import { useAutoGrowTextArea } from 'src/modules/shared/hooks/use-auto-grow-text-area';
import type { TextAreaProps } from 'src/modules/shared/types/text-area.types';

export const textAreaSizeClasses: Record<NonNullable<ComponentSize>, string> = {
  '2xs': `py-1 px-1.5 ${editableInputTextSizeClasses['2xs']}`,
  xs: `py-1.5 px-2 ${editableInputTextSizeClasses.xs}`,
  sm: `py-2 px-2.5 ${editableInputTextSizeClasses.sm}`,
  md: `py-2 px-3.5 ${editableInputTextSizeClasses.md}`,
  lg: `py-3 px-4.5 ${editableInputTextSizeClasses.lg}`,
};

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className = '',
      variant = 'primary',
      size = 'md',
      disabled = false,
      error,
      resize = 'none',
      placeholder,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      autoGrow = false,
      maxRows,
      heightBoundary,
      ...rest
    },
    ref
  ) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const [isEmpty, setIsEmpty] = useState(true);

    const checkIfEmpty = () => {
      if (internalRef.current) {
        setIsEmpty(internalRef.current.value === '');
      }
    };

    useEffect(() => {
      checkIfEmpty();
    }, [value, defaultValue]);

    const autoResize = useAutoGrowTextArea({
      elementRef: internalRef,
      enabled: autoGrow,
      maxRows,
      heightBoundary,
    });

    // Ensure correct height on mount and when value changes programmatically
    useLayoutEffect(() => {
      autoResize();
    }, [autoResize]);

    useEffect(() => {
      autoResize();
    }, [value, defaultValue, autoGrow, autoResize]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      checkIfEmpty();
      // Adjust height after content changes in uncontrolled scenarios
      autoResize();

      if (onChange) {
        onChange(e);
      }
    };

    const classes = [
      ...baseInputClasses,
      inputVariantClasses[variant],
      textAreaSizeClasses[size],
      getDisabledClasses(disabled),
      getErrorClasses(!!error),
      'border',
      `resize-${resize}`,
      autoGrow ? '!transition-colors' : '',
      className,
    ].join(' ');

    const textPlaceholder =
      typeof placeholder === 'string' ? placeholder : undefined;
    const showCustomPlaceholder = placeholder && !textPlaceholder && isEmpty;

    return (
      <div className="flex min-w-0 flex-1 flex-col w-full relative">
        <textarea
          ref={mergeRefs(internalRef, ref)}
          className={classes}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          placeholder={textPlaceholder}
          onFocus={(event) => {
            checkIfEmpty();
            autoResize();
            onFocus?.(event);
          }}
          onBlur={(event) => {
            checkIfEmpty();
            onBlur?.(event);
          }}
          {...rest}
        />

        {showCustomPlaceholder && (
          <div className="absolute top-0 left-0 right-0 bottom-0 text-neutral-400 pointer-events-none px-[1rem] py-[0.6rem] overflow-hidden">
            {placeholder}
          </div>
        )}

        <InputError message={error} />
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
